import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Autosave for the stage tables below the stage tree — owner decision 2026-10-09: the
 * operator saves by moving on (Enter / Tab / click), never by scrolling up to the toolbar.
 *
 * - `trigger: "cell"` (РН): leaving any edited cell saves.
 * - `trigger: "row"` (Норматив): saves only when focus moves to ANOTHER row or out of the
 *   tables — every normativ save is a GMP history entry, so one per finished row, not per cell.
 *   Focus moving into a search modal / dropdown overlay (a `.fixed` layer) does not count.
 *
 * Wired as `onBlurCapture` on the panel holding the tables. A short debounce folds quick
 * Tab-Tab into one save; the screen's own save path keeps its guards (a blocked save just
 * leaves the screen dirty for the manual Save).
 */
export type StageAutosaveStatus = "saving" | "saved" | null;

interface Options {
  enabled: boolean;
  isDirty: boolean;
  trigger: "cell" | "row";
  /** The screen's save path in quiet mode; resolves true on success. */
  save: () => Promise<boolean>;
}

const DEBOUNCE_MS = 400;
const SAVED_MARK_MS = 2000;

export const useStageAutosave = ({ enabled, isDirty, trigger, save }: Options) => {
  const [status, setStatus] = useState<StageAutosaveStatus>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const saveRef = useRef(save);
  saveRef.current = save;
  const debounceRef = useRef<number | undefined>(undefined);
  const savedTimerRef = useRef<number | undefined>(undefined);

  // Never two saves at once (the normativ PUT carries the version it was read at): a request
  // during a running save becomes ONE follow-up save with the latest values.
  const runningRef = useRef(false);
  const pendingRef = useRef(false);

  useEffect(() => () => {
    window.clearTimeout(debounceRef.current);
    window.clearTimeout(savedTimerRef.current);
  }, []);

  const runSave = useCallback(async () => {
    if (runningRef.current) {
      pendingRef.current = true;
      return;
    }
    if (!isDirtyRef.current) return;
    runningRef.current = true;
    window.clearTimeout(savedTimerRef.current);
    setStatus("saving");
    const ok = await saveRef.current().catch(() => false);
    runningRef.current = false;
    if (ok) {
      setStatus("saved");
      savedTimerRef.current = window.setTimeout(() => setStatus(null), SAVED_MARK_MS);
    } else {
      setStatus(null);
    }
    if (pendingRef.current) {
      pendingRef.current = false;
      // Wait one tick so isDirtyRef reflects the state committed after this save.
      window.setTimeout(() => { void runSave(); }, 0);
    }
  }, []);

  const onBlurCapture = useCallback((e: React.FocusEvent<HTMLElement>) => {
    // Any focusable element counts (input, the ident search button, a contenteditable div…):
    // Tab often lands on a cell's button, and leaving the row from there must still save.
    if (!enabled || !(e.target instanceof HTMLElement)) return;
    if (trigger === "row") {
      const next = e.relatedTarget instanceof HTMLElement ? e.relatedTarget : null;
      if (next?.closest(".fixed")) return;
      const inPanel = !!next && e.currentTarget.contains(next);
      if (inPanel && next?.closest("tr") === e.target.closest("tr")) return;
    }
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => { void runSave(); }, DEBOUNCE_MS);
  }, [enabled, trigger, runSave]);

  return { onBlurCapture, status };
};

export default useStageAutosave;
